<template>
  <div class="ats-page">
    <!-- 首屏区域：透明导航叠在背景上（与稿面一致） -->
    <div class="ats-hero-header">
      <section class="ats-hero">
        <AtsHeroBackground class="ats-hero__bg" :src="assets.heroBg" fallback-color="#0c1824" />
        <div class="ats-hero__mask" />
        <div class="ats-hero__content">
          <img
            class="ats-hero__tagline"
            :src="assets.heroTagline"
            alt="大救星 有求必救"
            width="367"
            height="51"
            loading="eager"
            decoding="sync"
          >
          <img
            class="ats-hero__title"
            :src="assets.heroTitle"
            alt="全球综合救援智能云平台"
            width="990"
            height="91"
            loading="eager"
            decoding="sync"
          >
          <ul class="ats-hero__pills" aria-label="核心能力">
            <li v-for="item in heroIcons" :key="item.label" class="ats-hero__pill">
              <img class="ats-hero__pill-icon" :src="item.src" :alt="item.label" width="50" height="50" loading="eager" decoding="async">
              <span class="ats-hero__pill-text">{{ item.label }}</span>
            </li>
          </ul>
        </div>
      </section>
    </div>

    <!-- 大救星全球救援服务：左文 + 中栏数据 + 右图（对齐设计稿） -->
    <section id="section-about" class="ats-section ats-section--surface ats-section--about">
      <div class="ats-about__text">
        <h2 class="ats-h2 ats-h2--on-light ats-h2--bar ats-about__title">大救星全球救援服务</h2>
        <img
          class="ats-about__divider"
          :src="assets.aboutDivider"
          alt=""
          role="presentation"
          width="600"
          height="10"
          decoding="async"
        />
        <p class="ats-body">
          大救星全球综合救援智能云平台由江泰救援服务有限公司研发打造，依托移动互联网技术，整合国内外优质资源、搭建共享经济协作网络，致力于为机构与个人提供一站式医疗救援与健康管理服务。
        </p>
        <p class="ats-body">
          以创新商业模式贯通「安全—健康—生活」全链条能力，构筑可信赖、可持续的一站式服务生态。
        </p>
        <button
          type="button"
          class="ats-btn ats-btn--about-chip"
          :style="aboutCtaBgStyle"
          @click="scrollTo('section-rescue')"
        >
          了解更多
          <span class="ats-btn__gt" aria-hidden="true">↗</span>
        </button>
      </div>
      <div class="ats-about__stats-wrap">
        <ul class="ats-about__stats" aria-label="平台数据">
          <li
            v-for="s in aboutStats"
            :key="s.label"
            :class="{ 'ats-about__stat-item--accent': s.accent }"
          >
            <strong class="ats-about__stat-value">{{ s.value }}</strong>
            <span class="ats-about__stat-label">{{ s.label }}</span>
          </li>
        </ul>
      </div>
      <div class="ats-about__visual">
        <div class="ats-about__building">
          <span class="ats-about__deco ats-about__deco--blue" aria-hidden="true" />
          <span class="ats-about__deco ats-about__deco--green" aria-hidden="true" />
          <img
            class="ats-about__building-img"
            :src="assets.building"
            alt="现代商务楼宇"
            loading="lazy"
          />
        </div>
      </div>
    </section>

    <!-- 全球救援：底图 bg_china_map_relief_dark（无 CSS 叠色） -->
    <section
      id="section-advantage"
      class="ats-section ats-section--advantage"
      :style="advantageSectionStyle"
    >
      <div class="ats-section__visual ats-section__visual--left">
        <img
          class="ats-advantage__illus"
          :src="assets.illusGlobalRescue"
          alt="全球救援平台示意图"
          width="600"
          height="600"
          loading="lazy"
        >
      </div>
      <div class="ats-section__text">
        <h2 class="ats-h2 ats-h2--on-light ats-h2--bar">全球救援</h2>
        <p class="ats-body">
          平台依托全球60余万个服务网点、3000余名双语星使，通过APP小程序 及多端协同系统，为用户提供7×24小时一键求助、实时定位快速响应， 每年服务覆盖人群超5000万。
        </p>
        <ul class="ats-mini-grid ats-mini-grid--on-light ats-mini-grid--advantage-points">
          <li v-for="x in globalRescuePoints" :key="x.t">
            <img :src="x.icon" :alt="x.t" width="50" height="60" loading="lazy">
            <span>{{ x.t }}</span>
          </li>
        </ul>
        <button
          type="button"
          class="ats-btn ats-btn--advantage-pill"
          :style="advantageLearnMoreBtnStyle"
          @click="scrollTo('section-rescue')"
        >
          了解更多
          <span class="ats-btn__gt" aria-hidden="true">→</span>
        </button>
      </div>
    </section>

    <!-- 救援清单 -->
    <section
      id="section-rescue"
      class="ats-section ats-section--rescue"
      :style="rescueSectionStyle"
    >
      <div class="ats-section__text">
        <h2 class="ats-h2 ats-h2--bar ats-h2--on-light">救援清单</h2>
        <p class="ats-body">
          大救星首创“救援+保险”服务清单产品，以清单为载体，助力景区、滑雪场、旅行 社等流量密集场景构建“防、保、救、调、赔”五位一体旅游安全应急救援保障体 系。借助市场化手段帮助客户解决落实保障体系建设的资金及技术方案等。目前 已为国内上百家景区、滑雪场、旅行社提供服务，获得广泛好评。
        </p>
        <ul class="ats-service-grid ats-service-grid--checks">
          <li
            v-for="svc in rescueServices"
            :key="svc.name"
            class="ats-rescue-svc__row"
            :style="rescueListRowBgStyle"
          >
            <img
              class="ats-rescue-svc__connector"
              :src="assets.uiConnectorLineLightB"
              alt=""
              role="presentation"
              decoding="async"
            >
            <span class="ats-rescue-svc__label">{{ svc.name }}</span>
          </li>
        </ul>
        <button
          type="button"
          class="ats-btn ats-btn--advantage-pill"
          :style="advantageLearnMoreBtnStyle"
          @click="goRescue"
        >
          了解更多
          <span class="ats-btn__gt" aria-hidden="true">→</span>
        </button>
      </div>
      <div class="ats-section__visual">
        <img
          class="ats-rescue__illus"
          :src="assets.illusRescueList"
          alt="救援清单数据示意"
          width="600"
          height="600"
          loading="lazy"
        >
      </div>
    </section>

    <!-- 健康监测：布局对齐「全球救援」，底图 `section_first_aid_laboratory_bg` -->
    <section
      id="section-health"
      class="ats-section ats-section--health"
      :style="healthSectionStyle"
    >
      <div class="ats-section__visual ats-section__visual--left">
        <img
          class="ats-health__illus"
          :src="assets.illusHealth"
          alt="健康监测设备示意"
          width="600"
          height="600"
          loading="lazy"
        >
      </div>
      <div class="ats-section__text">
        <h2 class="ats-h2 ats-h2--on-light ats-h2--bar">健康监测</h2>
       
        <ul class="ats-health-points" aria-label="健康监测能力">
          <li
            v-for="h in healthPoints"
            :key="h.t"
            class="ats-health-points__row"
            :style="healthPillRowBgStyle"
          >
            <img
              class="ats-health-points__lead"
              :src="assets.uiConnectorLineLightA"
              alt=""
              role="presentation"
              width="24"
              height="24"
              decoding="async"
            >
            <span class="ats-health-points__label">{{ h.t }}</span>
          </li>
        </ul>
         <p class="ats-body">
          依托“人工智能 + 大救星健康版App”，通过保险保障+智能设备+健康监测 预警系统+全球救援网络的大救星智能云平台，为用户提供保险转嫁、健康 监测、疾病预警、医疗咨询、医疗救援、医疗费用直付/垫付、院后康复、 找药送药、慢病管理等综合服务，实现对用户全生命健康周期的管理。
        </p>
        <button
          type="button"
          class="ats-btn ats-btn--advantage-pill"
          :style="advantageLearnMoreBtnStyle"
          @click="scrollTo('section-aid')"
        >
          了解更多
          <span class="ats-btn__gt" aria-hidden="true">→</span>
        </button>
      </div>
    </section>

    <!-- 急救包产品：版式参考「救援清单」，底图 `section_health_heartbeat_watermark_bg` -->
    <section
      id="section-aid"
      class="ats-section ats-section--aid"
      :style="firstAidSectionStyle"
    >
      <div class="ats-section__text">
        <h2 class="ats-h2 ats-h2--bar ats-h2--on-light">急救包产品</h2>
        <p class="ats-body">
          提供各类急救包、防汛包等急救物品、设备的批量定制化（含logo）采购。 场景覆盖：家用、办公楼宇、工厂厂房、医疗、消防、防汛、户外、车载等。 性价比高，品质保障！
        </p>
      
        <button
          type="button"
          class="ats-btn ats-btn--advantage-pill"
          :style="advantageLearnMoreBtnStyle"
          @click="scrollTo('section-partners')"
        >
          了解更多
          <span class="ats-btn__gt" aria-hidden="true">→</span>
        </button>
      </div>
      <div class="ats-section__visual">
        <img
          class="ats-first-aid__illus"
          :src="assets.illusFirstAid"
          alt="急救包产品示意"
          width="600"
          height="600"
          loading="lazy"
        >
      </div>
    </section>

    <!-- 合作伙伴：标题叠放于 logo 墙图正中 -->
    <section id="section-partners" class="ats-partners">
      <div class="ats-partners__figure">
        <img
          class="ats-partners__img"
          :src="assets.partnersLogos"
          alt=""
          role="presentation"
          loading="lazy"
          decoding="async"
        >
        <h2 class="ats-partners__title">合作伙伴</h2>
      </div>
    </section>

    <!-- 页脚：对齐设计稿，底图 `dibu`；二维码复用全站 `indexFooter` 素材 -->
    <AtsSiteFooter />
    <!-- legacy footer removed: use AtsSiteFooter -->
    <footer v-if="false" class="ats-footer" :style="footerStyle">
      <div class="ats-footer__inner">
        <div class="ats-footer__top">
          <router-link class="ats-footer__logo-link" to="/" title="返回官网首页">
            <img
              class="ats-footer__logo"
              :src="assets.footerDibuLogo"
              alt="大救星"
              width="160"
              height="48"
              decoding="async"
            >
          </router-link>
          <button
            type="button"
            class="ats-footer__platform-btn"
            :style="footerPlatformBtnStyle"
            @click="scrollTo('section-about')"
          >
            <span class="ats-footer__platform-btn__label">全球综合救援智能云平台</span>
          </button>
        </div>
        <div class="ats-footer__main">
          <div class="ats-footer__cols">
            <div class="ats-footer__col ats-footer__col--accent">
              <h3 class="ats-footer__col-title">用户登录</h3>
              <img
                class="ats-footer__col-rule-img"
                :src="assets.footerGreenLine"
                alt=""
                role="presentation"
                decoding="async"
              >
              <ul class="ats-footer__links">
                <li><button type="button" class="ats-footer__link" @click="onFooterLink(1)">救援机构</button></li>
                <li><button type="button" class="ats-footer__link" @click="onFooterLink(2)">监管机构</button></li>
                <li><button type="button" class="ats-footer__link" @click="onFooterLink(3)">指挥中心</button></li>
              </ul>
            </div>
            <div class="ats-footer__col">
              <h3 class="ats-footer__col-title">关于我们</h3>
              <img
                class="ats-footer__col-rule-img"
                :src="assets.footerGrayLine"
                alt=""
                role="presentation"
                decoding="async"
              >
              <ul class="ats-footer__links">
                <li><button type="button" class="ats-footer__link" @click="onFooterLink(4)">公司简介</button></li>
                <li><button type="button" class="ats-footer__link" @click="onFooterLink(5)">新闻中心</button></li>
                <li><button type="button" class="ats-footer__link" @click="onFooterLink(6)">合作咨询</button></li>
              </ul>
            </div>
            <div class="ats-footer__col">
              <h3 class="ats-footer__col-title">联系我们</h3>
              <img
                class="ats-footer__col-rule-img"
                :src="assets.footerGrayLine"
                alt=""
                role="presentation"
                decoding="async"
              >
              <ul class="ats-footer__links">
                <li><button type="button" class="ats-footer__link" @click="onFooterLink(7)">意见反馈</button></li>
                <li><span class="ats-footer__text">客服热线：+86-10-83423665</span></li>
                <li><span class="ats-footer__text">指挥中心：+86-10-83423665</span></li>
              </ul>
            </div>
          </div>
          <div class="ats-footer__qr-group">
            <div class="ats-footer__qr">
              <img
                :src="assets.qrWechatOfficial"
                alt="大救星微信公众号二维码"
                width="120"
                height="120"
                loading="lazy"
                decoding="async"
              >
              <p class="ats-footer__qr-label">微信公众号</p>
            </div>
            <div class="ats-footer__qr">
              <img
                :src="assets.qrAppDownload"
                alt="下载大救星 App 二维码"
                width="120"
                height="120"
                loading="lazy"
                decoding="async"
              >
              <p class="ats-footer__qr-label">下载大救星</p>
            </div>
          </div>
        </div>
        <div class="ats-footer__bottom">
          <p class="ats-footer__copy">大救星版权所有©2018-2024</p>
          <a
            class="ats-footer__icp"
            href="http://beian.miit.gov.cn/"
            target="_blank"
            rel="noopener noreferrer"
          >备案号：京ICP备2025140282号-1</a>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import util from '@/util/util.js'
import AtsSiteFooter from '@/components/ats/AtsSiteFooter.vue'
import AtsHeroBackground from '@/components/ats/AtsHeroBackground.vue'

/**
 * 品牌落地页：素材来自 `src/assets/AbouttheSavior`（已由脚本统一英文命名）
 */
export default {
  name: 'AboutTheSavior',
  components: {
    AtsSiteFooter,
    AtsHeroBackground
  },
  data () {
    return {
      /** @type {Record<string, string>} 本地静态资源路径（经 webpack 处理） */
      assets: {
        heroBg: require('@/assets/AbouttheSavior/hero_bg_digital_city.png'),
        heroTagline: require('@/assets/AbouttheSavior/djx-skAndItShallBeGiven.png'),
        heroTitle: require('@/assets/AbouttheSavior/IntelligentCloudPlatform.png'),
        building: require('@/assets/AbouttheSavior/section_global_services_building.png'),
        illusGlobalRescue: require('@/assets/AbouttheSavior/illustration_global_rescue_platform.png'),
        illusRescueList: require('@/assets/AbouttheSavior/illustration_rescue_list_data_stack.png'),
        illusHealth: require('@/assets/AbouttheSavior/illustration_health_monitoring_device.png'),
        illusFirstAid: require('@/assets/AbouttheSavior/illustration_first_aid_kit.png'),
        iconPlanet: require('@/assets/AbouttheSavior/icon_planet_global_network.png'),
        iconPulse: require('@/assets/AbouttheSavior/icon_hero_emergency_pulse.png'),
        iconShield: require('@/assets/AbouttheSavior/icon_hero_shield_security.png'),
        iconGem: require('@/assets/AbouttheSavior/icon_hero_smart_core_gem.png'),
        iconModular: require('@/assets/AbouttheSavior/icon_hero_modular_command.png'),
        iconSos: require('@/assets/AbouttheSavior/icon_sos_globe_emergency.png'),
        iconVoice: require('@/assets/AbouttheSavior/icon_voice_command_blue.png'),
        /** 「全球救援」四宫格：综合服务配图 */
        brandToolWatermark: require('@/assets/AbouttheSavior/brand_tool_watermark.png'),
        firstAidBg: require('@/assets/AbouttheSavior/section_first_aid_laboratory_bg.png'),
        /** 「急救包产品」区块底图（心跳水印） */
        sectionHeartbeatWatermarkBg: require('@/assets/AbouttheSavior/section_health_heartbeat_watermark_bg.png'),
        footerMountains: require('@/assets/AbouttheSavior/bg_mountains_dark_gradient.png'),
        /** 「全球救援」区块底图 */
        chinaMapReliefDark: require('@/assets/AbouttheSavior/bg_china_map_relief_dark.png'),
        /** 「大救星全球救援服务」标题下分隔线 */
        aboutDivider: require('@/assets/AbouttheSavior/ui_divider_progress_green.png'),
        /** 「大救星全球救援服务」了解更多按钮背景切片 */
        aboutChipBg: require('@/assets/AbouttheSavior/ui_chip_green_rounded.png'),
        /** 「全球救援」了解更多按钮背景切片 */
        advantagePillBg: require('@/assets/AbouttheSavior/ui_pill_black_green_border.png'),
        /** 「救援清单」服务项行背景 */
        uiSpacerBlackWide: require('@/assets/AbouttheSavior/ui_spacer_black_wide.png'),
        /** 「救援清单」服务项文案前装饰线 */
        uiConnectorLineLightB: require('@/assets/AbouttheSavior/ui_connector_line_light_b.png'),
        /** 「健康监测」要点行背景 */
        uiPillDarkNavy: require('@/assets/AbouttheSavior/ui_pill_dark_navy.png'),
        /** 「健康监测」要点文案前装饰线 */
        uiConnectorLineLightA: require('@/assets/AbouttheSavior/ui_connector_line_light_a.png'),
        /** 页脚底图 `dibu` */
        footerDibu: require('@/assets/AbouttheSavior/dibu.png'),
        /** 页脚左上角 Logo（`dibulog`） */
        footerDibuLogo: require('@/assets/AbouttheSavior/dibulog.png'),
        /** 页脚「用户登录」栏标题下装饰线 */
        footerGreenLine: require('@/assets/AbouttheSavior/greenline.png'),
        /** 页脚其余栏标题下装饰线 */
        footerGrayLine: require('@/assets/AbouttheSavior/grayline.png'),
        /** 页脚「全球综合救援智能云平台」按钮底图 */
        footerCloudPlatform: require('@/assets/AbouttheSavior/cloudplatform.png'),
        /** 「合作伙伴」logo 墙 */
        partnersLogos: require('@/assets/AbouttheSavior/section_partners_logos_grid.png'),
        /** 微信公众号二维码（`indexFooter` → `wxgzh.png`） */
        qrWechatOfficial: require('@/components/img/wxgzh.png'),
        /** 下载 App 二维码（`indexFooter` → `appgzh.png`） */
        qrAppDownload: require('@/components/img/appgzh.png')
      }
    }
  },
  computed: {
    /**
     * 页脚背景：`dibu` 浅色波纹底图
     * @returns {{ backgroundColor: string, backgroundImage: string, backgroundSize: string, backgroundPosition: string, backgroundRepeat: string }}
     */
    footerStyle () {
      return {
        backgroundColor: '#ffffff',
        backgroundImage: `url(${this.assets.footerDibu})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center bottom',
        backgroundRepeat: 'no-repeat'
      }
    },
    /**
     * 页脚「全球综合救援智能云平台」按钮背景（`cloudplatform`）；按钮尺寸 260×60，字重常规（见 `.ats-footer__platform-btn`）
     * @returns {{ backgroundImage: string, backgroundSize: string, backgroundRepeat: string, backgroundPosition: string, backgroundColor: string }}
     */
    footerPlatformBtnStyle () {
      return {
        backgroundColor: 'transparent',
        backgroundImage: `url(${this.assets.footerCloudPlatform})`,
        backgroundSize: '100% 100%',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center'
      }
    },
    /**
     * 「健康监测」`#section-health`：底图 `section_first_aid_laboratory_bg` + 纯白衬底（与「全球救援」区块写法一致）
     * @returns {{ backgroundColor: string, backgroundImage: string, backgroundSize: string, backgroundPosition: string, backgroundRepeat: string }}
     */
    healthSectionStyle () {
      const bg = this.assets.firstAidBg
      return {
        backgroundColor: '#ffffff',
        backgroundImage: `url(${bg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }
    },
    /**
     * 「急救包产品」`#section-aid`：底图 `section_health_heartbeat_watermark_bg` + 纯白衬底（与「救援清单」`rescueSectionStyle` 一致）
     * @returns {{ backgroundColor: string, backgroundImage: string, backgroundSize: string, backgroundPosition: string, backgroundRepeat: string }}
     */
    firstAidSectionStyle () {
      const bg = this.assets.sectionHeartbeatWatermarkBg
      return {
        backgroundColor: '#ffffff',
        backgroundImage: `url(${bg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }
    },
    /**
     * 「全球救援」`#section-advantage`：底图 `bg_china_map_relief_dark` + 纯白衬底（避免透明区发灰）
     * @returns {{ backgroundColor: string, backgroundImage: string, backgroundSize: string, backgroundPosition: string, backgroundRepeat: string }}
     */
    advantageSectionStyle () {
      const bg = this.assets.chinaMapReliefDark
      return {
        backgroundColor: '#ffffff',
        backgroundImage: `url(${bg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }
    },
    /**
     * 「救援清单」`#section-rescue`：底图 `bg_mountains_dark_gradient` + 纯白衬底
     * @returns {{ backgroundColor: string, backgroundImage: string, backgroundSize: string, backgroundPosition: string, backgroundRepeat: string }}
     */
    rescueSectionStyle () {
      const bg = this.assets.footerMountains
      return {
        backgroundColor: '#ffffff',
        backgroundImage: `url(${bg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }
    },
    /** @returns {Array<{ label: string, src: string }>} */
    heroIcons () {
      const a = this.assets
      return [
        { label: '科技赋能', src: a.iconModular },
        { label: '救援服务', src: a.iconShield },
        { label: '健康监测', src: a.iconPulse },
        { label: '保险保障', src: a.iconGem }
      ]
    },
    /**
     * 「大救星全球救援服务」模块数据（与设计稿一致）
     * @returns {Array<{ value: string, label: string, accent?: boolean }>}
     */
    aboutStats () {
      return [
        { value: '500+', label: '保司企业客户', accent: false },
        { value: '16300W+', label: '付费终端用户', accent: true },
        { value: '967W+', label: '累计服务人数', accent: false },
        { value: '3259+', label: '累计服务人次', accent: false }
      ]
    },
    /**
     * 「了解更多」按钮背景（`ui_chip_green_rounded`）
     * @returns {{ backgroundImage: string }}
     */
    aboutCtaBgStyle () {
      return {
        backgroundImage: `url(${this.assets.aboutChipBg})`
      }
    },
    /**
     * 「全球救援」「救援清单」「健康监测」了解更多按钮共用背景（`ui_pill_black_green_border`）
     * @returns {{ backgroundImage: string }}
     */
    advantageLearnMoreBtnStyle () {
      return {
        backgroundImage: `url(${this.assets.advantagePillBg})`
      }
    },
    /**
     * 「救援清单」「急救包产品」服务项行背景（`ui_spacer_black_wide`）
     * @returns {{ backgroundColor: string, backgroundImage: string, backgroundSize: string, backgroundRepeat: string, backgroundPosition: string }}
     */
    rescueListRowBgStyle () {
      return {
        backgroundColor: 'transparent',
        backgroundImage: `url(${this.assets.uiSpacerBlackWide})`,
        backgroundSize: '100% 100%',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center'
      }
    },
    /**
     * 「健康监测」要点行背景（`ui_pill_dark_navy`）；行宽高由样式类控制（290×50）
     * @returns {{ backgroundColor: string, backgroundImage: string, backgroundSize: string, backgroundRepeat: string, backgroundPosition: string }}
     */
    healthPillRowBgStyle () {
      return {
        backgroundColor: 'transparent',
        backgroundImage: `url(${this.assets.uiPillDarkNavy})`,
        backgroundSize: '100% 100%',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center'
      }
    },
    /** @returns {Array<{ t: string, icon: string }>} */
    globalRescuePoints () {
      const a = this.assets
      return [
        { t: '全球覆盖', icon: a.iconPlanet },
        { t: '一键救援', icon: a.iconSos },
        { t: '双语星使', icon: a.iconVoice },
        { t: '综合服务', icon: a.brandToolWatermark }
      ]
    },
    /** @returns {Array<{ name: string, icon: string }>} */
    rescueServices () {
      const a = this.assets
      return [
        { name: '无用户年龄限制', icon: a.iconSos },
        { name: '无既往病史限制', icon: a.iconPulse },
        { name: '无风险运动限制', icon: a.iconShield },
        { name: '无地理区域限制', icon: a.iconModular },
        { name: '无救援次数限制', icon: a.iconPlanet },
        { name: '无救援额度限制', icon: a.iconGem }
      ]
    },
    /** @returns {Array<{ t: string, icon: string }>} */
    healthPoints () {
      const a = this.assets
      return [
        { t: '精准守护、智能相伴', icon: a.iconGem },
        { t: '6个“24小时”专属服务', icon: a.iconModular },
        { t: '慢病健康全面呵护', icon: a.iconShield },
        { t: '全生命周期健康管理', icon: a.iconVoice }
      ]
    },
    /**
     * 「急救包产品」清单条目（版式对齐「救援清单」服务条）
     * @returns {Array<{ name: string }>}
     */
    firstAidKitServices () {
      return [
        { name: '家庭/车载多场景规格' },
        { name: '院前物资分类分区收纳' },
        { name: '图文使用说明与注意事项' },
        { name: '符合常用团体配置参考' },
        { name: '关键耗材独立密封包装' },
        { name: '便于补充与批次管理追溯' }
      ]
    }
  },
  methods: {
    /**
     * 顶部导航点击：高亮 + 滚动 / 路由 / 新窗口
     * @param {{ id: string, kind: string, target: string }} item - 导航项
     * @returns {void}
     */
    /**
     * 平滑滚动到指定锚点区块
     * @param {string} id - 不带 # 的 DOM id
     * @returns {void}
     */
    scrollTo (id) {
      const el = document.getElementById(id)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    /**
     * 进入救援相关能力页（沿用站点已有路由）
     * @returns {void}
     */
    goRescue () {
      this.$router.push({ path: '/rescue-list' })
    },
    /**
     * 页脚链接跳转（逻辑对齐 `indexFooter`）
     * @param {number} id - 链接类型：1–3 开放平台登录；4–5 公司介绍；6 合作咨询；7 意见反馈
     * @returns {void}
     */
    onFooterLink (id) {
      const url = util.config.url
      if (id === 1 || id === 2 || id === 3) {
        window.location.href = `${url}/djxwork/#/`
      } else if (id === 4) {
        this.$router.push({ path: '/aboutus', query: { activeName: 'about' } })
        if (this.$root.bus) {
          this.$root.bus.$emit('only', 5)
          this.$root.bus.$emit('about', 'about')
        }
      } else if (id === 5) {
        this.$router.push({ path: '/aboutus', query: { activeName: 'news' } })
        if (this.$root.bus) {
          this.$root.bus.$emit('only', 5)
          this.$root.bus.$emit('about', 'news')
        }
      } else if (id === 6) {
        window.location.href = `${url}/djxwork/#/contact`
      } else if (id === 7) {
        window.location.href = `${url}/djxwork/#/feedback`
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../components/ats/mixins';
$green: #0d3d2e;
$green-light: #1a7a5c;
$accent: #18a06a;
$mint: #8fe9c9;
$text-dark: #1a2226;
$body-grey: #333333;
$body-muted: #6d7882;
$text-on-dark: #e8f4ef;

.ats-page {
  min-height: 100vh;
  background: #eef2f0;
  color: $text-dark;
  font-family: 'Microsoft YaHei', 'PingFang SC', 'Helvetica Neue', sans-serif;
  -webkit-font-smoothing: antialiased;
}

.ats-hero-header {
  position: relative;
  height: 871px;
  min-height: 871px;
  max-height: 871px;
}

.ats-nav--overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 40;
  height: 100px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  background: transparent;
  border: none;
  box-shadow: none;
}

.ats-nav__inner {
  width: 100%;
  max-width: 1320px;
  height: 100%;
  margin: 0 auto;
  padding: 0 36px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.ats-brand {
  display: inline-flex;
  align-items: baseline;
  flex-shrink: 0;
  color: #fff;
  text-decoration: none;
  font-weight: 700;
  letter-spacing: 0.12em;
  font-size: 22px;
  line-height: 1;
  text-shadow: 0 1px 12px rgba(0, 0, 0, 0.25);
}

.ats-brand__logo-text {
  font-weight: 700;
}

.ats-brand__logo-last {
  position: relative;
  display: inline-block;
  font-weight: 700;
}

.ats-brand__star {
  position: absolute;
  top: -6px;
  right: -0.55em;
  font-size: 11px;
  font-style: normal;
  line-height: 1;
  color: #fff;
  text-shadow: 0 0 8px rgba(255, 255, 255, 0.55);
  pointer-events: none;
}

.ats-brand__mark {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: linear-gradient(135deg, $mint, #2a9d6a);
  box-shadow: 0 0 16px rgba(143, 233, 201, 0.35);
}

.ats-brand__mark--light {
  box-shadow: none;
  opacity: 0.95;
}

.ats-nav__links {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px 36px;
  a {
    position: relative;
    flex-shrink: 0;
    padding-bottom: 10px;
    color: #fff;
    text-decoration: none;
    font-size: 15px;
    font-weight: 500;
    letter-spacing: 0.04em;
    white-space: nowrap;
    opacity: 0.95;
    transition: opacity 0.2s;
    &::after {
      content: '';
      position: absolute;
      left: 50%;
      bottom: 0;
      transform: translateX(-50%) scaleX(0);
      width: 28px;
      height: 2px;
      border-radius: 2px;
      background: #fff;
      transition: transform 0.2s;
    }
    &:hover {
      opacity: 1;
    }
    &.is-active {
      opacity: 1;
      font-weight: 600;
      &::after {
        transform: translateX(-50%) scaleX(1);
      }
    }
  }
}

/* —— 首屏：稿面固定高度 871px；底图用 img + object-fit 保证满铺且比例正确 —— */
.ats-hero {
  position: relative;
  height: 100%;
  min-height: 871px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: #fff;
  overflow: hidden;
}

.ats-hero__bg {
  background-size: cover;
  background-position: center bottom;
}

.ats-hero__mask {
  position: absolute;
  inset: 0;
  z-index: 1;
  /* 几乎不挡底图：整体接近透明，仅底部极轻过渡，避免与下方浅色区块硬切 */
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.04) 0%,
    rgba(0, 0, 0, 0) 38%,
    rgba(0, 0, 0, 0) 62%,
    rgba(6, 14, 11, 0.12) 100%
  );
  pointer-events: none;
}

.ats-hero__content {
  position: relative;
  z-index: 2;
  max-width: 1040px;
  margin: 0 auto;
  width: 100%;
  padding: 0 28px;
  box-sizing: border-box;
  text-align: center;
}

.ats-hero__tagline {
  display: block;
  width: 367px;
  height: 51px;
  margin: 0 auto 50px;
  filter: drop-shadow(0 1px 4px rgba(0, 0, 0, 0.45)) drop-shadow(0 2px 16px rgba(0, 0, 0, 0.35));
}

.ats-hero__title {
  display: block;
  width: 875px;
  height: 74px;
  margin: 0 auto 50px;
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.5)) drop-shadow(0 4px 28px rgba(0, 0, 0, 0.4));
}

.ats-hero__pills {
  list-style: none;
  margin: 0 auto;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  align-content: center;
  gap: 16px 20px;
  width: fit-content;
  max-width: 100%;
  box-sizing: border-box;
}

.ats-hero__pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 0;
  padding: 12px 26px;
  border-radius: 999px;
  background: #fff;
  border: none;
  box-shadow: 0 10px 32px rgba(0, 0, 0, 0.14);
}

.ats-hero__pill-icon {
  width: 50px;
  height: 50px;
  object-fit: contain;
  flex-shrink: 0;
}

.ats-hero__pill-text {
  font-size: 20px;
  font-weight: 600;
  color: #000000;
  letter-spacing: 0.06em;
  white-space: nowrap;
}

/* —— 内容区：双栏比例约 44% / 56%，插图宽度收敛到 ~45% 视觉 —— */
.ats-section {
  margin: 0 auto;
  padding: 88px max(24px, calc((100% - 1455px) / 2));
  display: grid;
  grid-template-columns: minmax(0, 0.44fr) minmax(0, 0.56fr);
  gap: 48px 56px;
  align-items: center;
  max-width: none;
}

.ats-section--surface {
  background: #ffffff;
  border-top: 1px solid rgba(255, 255, 255, 0.65);
  border-bottom: 1px solid rgba(15, 61, 47, 0.06);
}

/** 「全球救援」「健康监测」：双栏间距与标题/正文（健康要点列表单独见 `.ats-health-points`） */
.ats-section--advantage,
.ats-section--health {
  gap: 48px 145px;
  .ats-section__text .ats-h2--on-light {
    font-size: 42px;
    font-weight: 700;
    line-height: 1.25;
  }
  .ats-body {
    font-size: 20px;
    line-height: 1.65;
    color: #000000;
    text-shadow: none;
  }
  .ats-h2--on-light {
    color: #000000;
    text-shadow: none;
  }
}

/** 「全球救援」四要点：一行展示，项间距 78px；配图 50×60 */
.ats-section--advantage {
  .ats-mini-grid--on-light li {
    font-size: 20px;
    line-height: 1.65;
    color: #000000;
    text-shadow: none;
  }
  .ats-mini-grid--advantage-points {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: space-between;
    gap: 32px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    list-style: none;
    margin: 0;
    padding: 0;
    li {
      display: inline-flex;
      flex-direction: row;
      align-items: center;
      flex-shrink: 0;
      gap: 12px;
    }
    img {
      width: 50px;
      height: 60px;
      object-fit: contain;
      flex-shrink: 0;
      display: block;
    }
  }
}

/** 「健康监测」四要点：一行两个；290×50，行间 20px，`ui_pill_dark_navy` + 文案前 `ui_connector_line_light_a`（24×24），字 20px / #4683FF；与标题同列左对齐 */
.ats-section--health .ats-health-points {
  display: grid;
  grid-template-columns: repeat(2, 290px);
  justify-content: start;
  column-gap: 18px;
  row-gap: 20px;
  list-style: none;
  margin: 0 0 28px;
  padding: 0;
  li.ats-health-points__row {
    width: 290px;
    height: 50px;
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    padding: 0 12px;
    margin: 0;
    border: none;
    border-radius: 0;
    box-shadow: none;
    background-color: transparent;
  }
  .ats-health-points__lead {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    object-fit: contain;
    display: block;
  }
  .ats-health-points__label {
    min-width: 0;
    font-size: 20px;
    font-weight: 600;
    line-height: 1.2;
    color: #4683FF;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

/** 「全球救援」「救援清单」「大救星全球救援服务」「健康监测」「急救包产品」标题：`ats-h2--bar` 右侧竖条（宽 10px、#256A30、距标题 30px） */
.ats-section--advantage .ats-h2--bar,
.ats-section--rescue .ats-h2--bar,
.ats-section--about .ats-h2--bar,
.ats-section--health .ats-h2--bar,
.ats-section--aid .ats-h2--bar {
  padding-left: 0;
  &::before {
    display: none;
  }
  &::after {
    content: '';
    display: inline-block;
    width: 10px;
    height: 0.85em;
    margin-left: 30px;
    vertical-align: middle;
    border-radius: 2px;
    background: #256a30;
  }
}

/** 「全球救援」「救援清单」「健康监测」「急救包产品」了解更多：同款 `ui_pill_black_green_border` 底图、字色 #256A30；与上方内容间距 40px */
.ats-section--advantage .ats-btn--advantage-pill,
.ats-section--rescue .ats-btn--advantage-pill,
.ats-section--health .ats-btn--advantage-pill,
.ats-section--aid .ats-btn--advantage-pill {
  margin-top: 40px;
  align-self: flex-start;
  border: none;
  border-radius: 0;
  background-color: transparent;
  background-repeat: no-repeat;
  background-position: center;
  background-size: 100% 100%;
  color: #256a30;
  font-size: 16px;
  font-weight: 600;
  padding: 14px 40px;
  box-shadow: none;
  .ats-btn__gt {
    color: #256a30;
  }
  @include ats-hover-pill(1.04);
}

/** 「救援清单」「急救包产品」：与「全球救援」同列间距；底图独立设置（见 `rescueSectionStyle` / `firstAidSectionStyle`），此处补原 `surface` 分隔线；服务条栅格共用 */
.ats-section--rescue,
.ats-section--aid {
  gap: 48px 145px;
  border-top: 1px solid rgba(255, 255, 255, 0.65);
  border-bottom: 1px solid rgba(15, 61, 47, 0.06);
  .ats-section__text .ats-h2 {
    font-size: 42px;
    font-weight: 700;
    line-height: 1.25;
  }
  .ats-h2--on-light,
  .ats-body {
    color: $text-dark;
  }
  .ats-body {
    font-size: 20px;
    line-height: 1.65;
  }
  /** 服务清单：每项 220×50，一行三个；黑宽条底图 + 文案前连接器图，整体居中 */
  .ats-service-grid--checks {
    display: grid;
    grid-template-columns: repeat(3, 220px);
    grid-auto-flow: row;
    justify-content: center;
    column-gap: 18px;
    row-gap: 12px;
    margin-bottom: 28px;
    li.ats-rescue-svc__row {
      width: 220px;
      height: 50px;
      box-sizing: border-box;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      gap: 6px;
      padding: 0 10px;
      margin: 0;
      font-size: 20px;
      font-weight: 600;
      line-height: 1.2;
      color: #256A30;
      border: none;
      border-radius: 0;
      box-shadow: none;
      background-color: transparent;
    }
    .ats-rescue-svc__connector {
      flex-shrink: 0;
      height: 18px;
      width: auto;
      max-width: 40%;
      object-fit: contain;
      display: block;
    }
    .ats-rescue-svc__label {
      min-width: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      text-align: center;
      color: #256A30;
    }
  }
}

/* —— 大救星全球救援服务：三栏（文案 | 数据 | 楼宇图），左缘距视口 260px —— */
.ats-section--about {
  padding: 88px 0 88px 260px;
  grid-template-columns: minmax(0, 1fr) auto 716px;
  column-gap: 48px;
  row-gap: 40px;
  align-items: center;
}

/** 右栏楼宇图贴齐区块右缘（本段已 padding-right: 0，即视口右缘） */
.ats-section--about .ats-about__visual {
  justify-self: end;
  margin-right: 0;
  min-width: 0;
}

.ats-section--about .ats-about__stats-wrap {
  min-width: 0;
}

.ats-section--about .ats-about__text {
  max-width: 540px;
}

.ats-section--about .ats-about__title {
  margin: 0 0 14px;
  font-size: 40px;
  font-weight: 700;
  line-height: 1.25;
}

/** 本模块正文（两段介绍） */
.ats-section--about .ats-about__text .ats-body {
  font-size: 20px;
  line-height: 1.75;
}

.ats-about__divider {
  display: block;
  width: 600px;
  height: 10px;
  max-width: 100%;
  margin: 0 0 22px;
  object-fit: contain;
  object-position: left center;
}

.ats-about__text .ats-body:last-of-type {
  margin-bottom: 28px;
}

.ats-about__stats {
  list-style: none;
  padding: 0;
}

.ats-about__stats li {
  margin-bottom: 22px;
  &:last-child {
    margin-bottom: 0;
  }
}

.ats-about__stat-value {
  display: block;
  font-size: 48px;
  font-weight: 800;
  color: #1a1f24;
  letter-spacing: 0.02em;
  line-height: 1.2;
}

.ats-about__stat-item--accent .ats-about__stat-value {
  color: #0d9f62;
}

.ats-about__stat-label {
  display: block;
  margin-top: 6px;
  font-size: 18px;
  color: $body-muted;
  line-height: 1.45;
}

.ats-about__visual {
  min-width: 0;
}

.ats-about__building {
  position: relative;
  width: 716px;
  height: 751px;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: 0;
  box-shadow: 0 16px 40px rgba(15, 45, 36, 0.12);
}

.ats-about__deco {
  position: absolute;
  pointer-events: none;
  z-index: 0;
}

.ats-about__deco--blue {
  width: 42%;
  height: 32%;
  top: 6%;
  left: 0;
  background: rgba(43, 127, 255, 0.88);
}

.ats-about__deco--green {
  width: 38%;
  height: 36%;
  top: 4%;
  right: 0;
  background: rgba(13, 61, 46, 0.55);
}

.ats-about__building-img {
  position: relative;
  z-index: 1;
  display: block;
  width: 716px;
  height: 751px;
  border-radius: 0;
  object-fit: cover;
}

.ats-section__text {
  width: 708px;
  box-sizing: border-box;
}

.ats-section__visual {
  display: flex;
  justify-content: center;
  align-items: center;
  img {
    width: 100%;
    max-width: 480px;
    height: auto;
    display: block;
  }
}

.ats-section__visual--left {
  order: -1;
}

/** 「全球救援」左侧平台示意图：600×600（`illustration_global_rescue_platform`） */
.ats-section--advantage .ats-advantage__illus,
.ats-section--health .ats-health__illus {
  width: 600px;
  height: 600px;
  max-width: 600px;
  object-fit: contain;
  flex-shrink: 0;
}

/** 「救援清单」右侧示意图：600×600（对齐全球救援插图）；左移留白由 margin-left 控制 */
.ats-section--rescue .ats-rescue__illus,
.ats-section--aid .ats-first-aid__illus {
  width: 600px;
  height: 600px;
  max-width: 600px;
  object-fit: contain;
  flex-shrink: 0;
  margin-left: 380px;
  display: block;
}

.ats-h2 {
  font-size: 34px;
  font-weight: 700;
  margin: 0 0 20px;
  line-height: 1.3;
  color: #fff;
}

.ats-h2--on-light {
  color: $text-dark;
}

.ats-h2--bar {
  position: relative;
  padding-left: 18px;
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 5px;
    bottom: 5px;
    width: 4px;
    border-radius: 2px;
    background: linear-gradient(180deg, #34d399, $green-light);
  }
}

.ats-body {
  margin: 0 0 16px;
  font-size: 17px;
  line-height: 1.85;
  color: $body-grey;
}

.ats-body:last-of-type {
  margin-bottom: 28px;
}

.ats-body--on-dark {
  color: rgba(232, 244, 239, 0.88);
}

.ats-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 999px;
  padding: 12px 28px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  @include ats-hover-pill(1.05);
}

.ats-btn--solid {
  background: $accent;
  color: #fff;
  box-shadow: 0 8px 22px rgba(24, 160, 106, 0.35);
}

/** 「大救星全球救援服务」了解更多：固定尺寸 + 绿色圆角切片底图 + 白字 */
.ats-btn--about-chip {
  width: 160px;
  height: 60px;
  padding: 0;
  justify-content: center;
  gap: 6px;
  border-radius: 0;
  background-color: transparent;
  background-repeat: no-repeat;
  background-position: center;
  background-size: 100% 100%;
  color: #fff;
  font-size: 20px;
  font-weight: 600;
  letter-spacing: 0.04em;
  box-shadow: none;
  .ats-btn__gt {
    color: #fff;
    font-size: 18px;
    font-weight: 700;
    margin-left: 0;
  }
  @include ats-hover-pill(1.06);
}

.ats-btn--outline {
  background: rgba(255, 255, 255, 0.08);
  color: $green-light;
  border: 1px solid rgba(24, 160, 106, 0.55);
  .ats-btn__gt {
    color: $green-light;
  }
}

.ats-btn__gt {
  font-size: 14px;
  font-weight: 700;
  margin-left: 2px;
}

.ats-mini-grid--on-light li {
  color: $text-dark;
}

.ats-mini-grid--2 {
  margin-bottom: 20px;
}

.ats-service-grid {
  display: grid;
  list-style: none;
  padding: 0;
  margin: 0;
}

.ats-service-grid--checks {
  gap: 12px 20px;
  margin-bottom: 28px;
}

.ats-check {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: linear-gradient(135deg, #34d399, $accent);
  position: relative;
  &::after {
    content: '';
    position: absolute;
    left: 5px;
    top: 3px;
    width: 5px;
    height: 9px;
    border: solid #fff;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
}

.ats-partners {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 56px 20px 72px;
  background-color: #ffffff;
  text-align: center;
}

/** 合作伙伴：logo 墙与叠字容器 */
.ats-partners__figure {
  position: relative;
  max-width: 1160px;
  width: 100%;
  margin: 0 auto;
}

/** 合作伙伴标题：叠放在 logo 墙图几何中心 */
.ats-partners__title {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  margin: 0;
  padding: 0 16px;
  box-sizing: border-box;
  max-width: 100%;
  font-size: 25px;
  font-weight: 700;
  line-height: 1.3;
  color: #ffffff;
  text-align: center;
  pointer-events: none;
}

.ats-partners__img {
  max-width: 1160px;
  width: 100%;
  height: auto;
  margin: 0 auto;
  display: block;
}

.ats-footer {
  color: $body-grey;
  padding: 0;
  background-color: #ffffff;
}

.ats-footer__inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 56px 48px 0;
  box-sizing: border-box;
}

.ats-footer__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 40px;
}

.ats-footer__logo-link {
  display: inline-flex;
  text-decoration: none;
}

.ats-footer__logo {
  display: block;
  width: auto;
  height: 48px;
  max-width: 180px;
  object-fit: contain;
}

.ats-footer__platform-btn {
  flex-shrink: 0;
  box-sizing: border-box;
  width: 260px;
  height: 60px;
  padding: 0 12px;
  border: none;
  border-radius: 0;
  margin: 0;
  appearance: none;
  -webkit-appearance: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #256A30;
  font-family: inherit;
  cursor: pointer;
  transition: filter 0.2s ease;
  &:hover {
    filter: brightness(1.05);
  }
}

/** 按钮内文案：常规字重，在 60px 高内由 flex 垂直居中 */
.ats-footer__platform-btn__label {
  display: block;
  max-width: 100%;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.35;
  text-align: center;
  white-space: normal;
  color: #256A30;
}

.ats-footer__main {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 32px 48px;
  padding-bottom: 40px;
}

.ats-footer__cols {
  flex: 1 1 420px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 28px 56px;
  min-width: 0;
}

.ats-footer__col-title {
  margin: 0 0 10px;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
  color: #333333;
}

.ats-footer__col--accent .ats-footer__col-title {
  color: #256a30;
}

/** 栏目标题下装饰线切片：`greenline` / `grayline` */
.ats-footer__col-rule-img {
  display: block;
  width: 100%;
  height: auto;
  margin: 0 0 18px;
  object-fit: contain;
}

.ats-footer__links {
  list-style: none;
  margin: 0;
  padding: 0;
  li + li {
    margin-top: 12px;
  }
}

.ats-footer__link {
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-size: 14px;
  line-height: 1.6;
  color: #666666;
  cursor: pointer;
  text-align: left;
  &:hover {
    color: #256a30;
  }
}

.ats-footer__text {
  display: block;
  font-size: 14px;
  line-height: 1.6;
  color: #666666;
}

.ats-footer__qr-group {
  display: flex;
  flex-shrink: 0;
  align-items: flex-start;
  gap: 40px;
}

.ats-footer__qr {
  text-align: center;
  img {
    display: block;
    width: 120px;
    height: 120px;
    object-fit: contain;
    margin: 0 auto;
  }
}

.ats-footer__qr-label {
  margin: 12px 0 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  color: #333333;
}

.ats-footer__bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 0 28px;
  border-top: 1px solid #e8e8e8;
}

.ats-footer__copy {
  margin: 0;
  font-size: 16px;
  line-height: 1.5;
  color: #333333;
}

.ats-footer__icp {
  font-size: 16px;
  line-height: 1.5;
  color: #333333;
  text-decoration: none;
  &:hover {
    color: #256a30;
  }
}

@media (max-width: 960px) {
  .ats-nav--overlay {
    height: auto;
    min-height: 72px;
    align-items: flex-start;
    padding: 12px 0;
  }
  .ats-nav__inner {
    flex-direction: column;
    align-items: stretch;
    padding: 0 20px;
    height: auto;
  }
  .ats-nav__links {
    flex-wrap: wrap;
    justify-content: flex-start;
    gap: 6px 14px;
    width: 100%;
    a {
      font-size: 13px;
      white-space: normal;
      padding-bottom: 6px;
    }
  }
  .ats-hero-header {
    height: auto;
    min-height: 480px;
    max-height: none;
  }
  .ats-hero {
    height: auto;
    min-height: 480px;
    max-height: none;
  }
  .ats-hero__pills {
    flex-direction: column;
    align-items: center;
    width: 100%;
    max-width: 100%;
  }
  .ats-hero__pill {
    justify-content: center;
    width: min(400px, 100%);
    box-sizing: border-box;
  }
  .ats-section {
    grid-template-columns: 1fr;
    padding-top: 56px;
    padding-bottom: 56px;
  }
  .ats-section--advantage .ats-mini-grid--advantage-points {
    flex-wrap: wrap;
    justify-content: center;
    row-gap: 20px;
    column-gap: 40px;
  }
  .ats-section--health .ats-health-points {
    grid-template-columns: minmax(0, 290px);
    justify-content: start;
    li.ats-health-points__row {
      width: min(290px, 100%);
    }
  }
  .ats-section--rescue .ats-service-grid--checks,
  .ats-section--aid .ats-service-grid--checks {
    grid-template-columns: minmax(0, 220px);
    justify-content: center;
  }
  .ats-section--about {
    grid-template-columns: 1fr;
    padding: 56px 20px;
  }
  .ats-section--about .ats-about__text {
    margin-left: 0;
  }
  .ats-section--about .ats-about__visual {
    justify-self: stretch;
    margin-right: 0;
  }
  .ats-about__building,
  .ats-about__building-img {
    width: min(716px, 100%);
    height: auto;
    max-height: none;
    aspect-ratio: 716 / 751;
  }
  .ats-about__building {
    max-width: 716px;
    margin-left: auto;
    margin-right: auto;
  }
  .ats-about__stats {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px 16px;
    margin-left: 0;
    margin-right: 0;
  }
  .ats-about__stats li {
    margin-bottom: 0;
  }
  .ats-section__visual img {
    max-width: min(400px, 100%);
  }
  .ats-section--advantage .ats-advantage__illus,
  .ats-section--health .ats-health__illus {
    width: 600px;
    height: 600px;
    max-width: 100%;
    object-fit: contain;
  }
  .ats-section--rescue .ats-rescue__illus,
  .ats-section--aid .ats-first-aid__illus {
    width: 600px;
    height: 600px;
    max-width: 100%;
    object-fit: contain;
    margin-left: 0;
    display: block;
  }
  .ats-section__visual--left {
    order: 0;
  }
  .ats-footer__inner {
    padding: 40px 20px 0;
  }
  .ats-footer__top {
    flex-direction: column;
    align-items: flex-start;
  }
  .ats-footer__main {
    flex-direction: column;
  }
  .ats-footer__cols {
    grid-template-columns: 1fr;
    flex: 1 1 100%;
  }
  .ats-footer__qr-group {
    width: 100%;
    justify-content: center;
  }
  .ats-footer__bottom {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/aboutTheSavior/aboutTheSavior.vue